import React from 'react';
import Icon from 'components/atoms/Icon';
import { ResultMessage as ResultMessageType } from 'types/chat.types';
import HTSClassificationResult from 'components/organisms/chat/HTSClassificationResult';
import TariffDeterminationResult from 'components/organisms/chat/TariffDeterminationResult';

interface ResultMessageProps {
  message: ResultMessageType;
}

const ResultMessage: React.FC<ResultMessageProps> = ({ message }) => {
  const { result, timestamp } = message;

  if (!result) {
    return null;
  }

  const hts = result.hts_classification;
  const hasMeaningfulResult = hts && hts.success !== false && hts.primary_hts_code;

  if (!hasMeaningfulResult && !result.tariff_determination && !result.sections) {
    return (
      <div className="self-start w-full flex flex-col">
        <div className="bg-gray-50 px-4 py-3 rounded-2xl rounded-bl-md max-w-3xl">
          <div className="flex items-center gap-2 text-xs text-gray-500">
            <div className="w-4 h-4 bg-amber-400 rounded-full flex items-center justify-center">
              <Icon name="information-circle" className="w-2.5 h-2.5 text-white" />
            </div>
            <span>Classification could not be completed. Please try again with more details.</span>
          </div>
        </div>
        <div className="text-[10px] text-gray-400 mt-1 ml-2">
          {timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    );
  }

  return (
    <div className="self-start w-full flex flex-col">
      <div className="bg-gray-50 px-4 py-4 rounded-2xl rounded-bl-md max-w-3xl">
        <div className="flex items-center gap-2 mb-4 text-xs text-gray-500">
          <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
            <Icon name="check" className="w-2.5 h-2.5 text-white" />
          </div>
          <span>Classification Complete</span>
        </div>

        {hasMeaningfulResult && (
          <HTSClassificationResult
            data={hts}
            classificationId={result.classification_id}
            documentId={result.document_id}
            countryOfOrigin={result.extracted_data?.gsp?.country_of_origin?.value}
            tariffData={result.tariff_determination}
            tariffLoading={result.tariff_loading}
          />
        )}

        {/* Fallback: a tariff determination with no nestable HTS result. */}
        {!hasMeaningfulResult && result.tariff_determination && !result.tariff_loading && (
          <TariffDeterminationResult data={result.tariff_determination} />
        )}

        {result.sections && (
          <div className="space-y-3">
            {Object.entries(result.sections).map(([sectionKey, section]: [string, any]) => {
              if (sectionKey === 'hts_classification' && section.success) {
                return (
                  <HTSClassificationResult
                    key={sectionKey}
                    data={section}
                    filename={result.filename}
                    classificationId={result.classification_id}
                    documentId={result.document_id}
                    countryOfOrigin={result.extracted_data?.gsp?.country_of_origin?.value}
                  />
                );
              }
              return null;
            })}
          </div>
        )}
      </div>
      <div className="text-xs text-gray-400 mt-1 ml-2">
        {timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
  );
};

export default ResultMessage;
